/**
 * Enhanced Offline Data Service
 * Provides IndexedDB storage for larger datasets and better conflict resolution
 */

const DB_NAME = 'forgetmenot_db';
const DB_VERSION = 1;

// Store names
const STORES = {
  documents: 'documents',
  contacts: 'contacts', 
  calendar: 'calendar',
  parking: 'parking',
  medical: 'medical',
  dates: 'dates',
  emergencyBinder: 'emergency_binder',
  pets: 'pets',
  syncQueue: 'sync_queue',
  metadata: 'metadata',
};

let dbInstance = null;

/**
 * Open/Initialize the IndexedDB database
 */
export const openDatabase = () => {
  return new Promise((resolve, reject) => {
    if (dbInstance) {
      resolve(dbInstance);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('IndexedDB error:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Create object stores
      Object.values(STORES).forEach(storeName => {
        if (!db.objectStoreNames.contains(storeName)) {
          const store = db.createObjectStore(storeName, { keyPath: 'id', autoIncrement: false });
          store.createIndex('updatedAt', 'updatedAt', { unique: false });
          store.createIndex('syncStatus', 'syncStatus', { unique: false });
        }
      });
    };
  });
};

/**
 * Get all items from a store
 */
export const getAllFromStore = async (storeName) => {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get a single item by ID
 */
export const getFromStore = async (storeName, id) => {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.get(id);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Save item to store
 */
export const saveToStore = async (storeName, item) => {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    
    const itemWithMeta = {
      ...item,
      updatedAt: new Date().toISOString(),
      syncStatus: item.syncStatus || 'synced',
    };
    
    const request = store.put(itemWithMeta);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Delete item from store
 */
export const deleteFromStore = async (storeName, id) => {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.delete(id);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

/**
 * Clear all items from a store
 */
export const clearStore = async (storeName) => {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.clear();

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get items pending sync
 */
export const getPendingSyncItems = async (storeName) => {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const index = store.index('syncStatus');
    const request = index.getAll('pending');

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Mark item as synced
 */
export const markAsSynced = async (storeName, id, serverData = null) => {
  const existing = await getFromStore(storeName, id);
  if (existing) {
    await saveToStore(storeName, {
      ...existing,
      ...(serverData || {}),
      syncStatus: 'synced',
      lastSyncedAt: new Date().toISOString(),
    });
  }
};

/**
 * Conflict Resolution Strategies
 */
export const ConflictStrategy = {
  SERVER_WINS: 'server_wins',
  CLIENT_WINS: 'client_wins', 
  LATEST_WINS: 'latest_wins',
  MERGE: 'merge',
};

/**
 * Resolve conflict between local and server data
 */
export const resolveConflict = (localItem, serverItem, strategy = ConflictStrategy.LATEST_WINS) => {
  switch (strategy) {
    case ConflictStrategy.SERVER_WINS:
      return { ...serverItem, syncStatus: 'synced' };
      
    case ConflictStrategy.CLIENT_WINS:
      return { ...localItem, syncStatus: 'pending' };
      
    case ConflictStrategy.LATEST_WINS:
      const localTime = new Date(localItem.updatedAt || 0).getTime();
      const serverTime = new Date(serverItem.updatedAt || serverItem.updated_at || 0).getTime();
      return localTime > serverTime 
        ? { ...localItem, syncStatus: 'pending' }
        : { ...serverItem, syncStatus: 'synced' };
      
    case ConflictStrategy.MERGE:
      // Merge fields, preferring non-null values
      const merged = { ...serverItem };
      Object.keys(localItem).forEach(key => {
        if (localItem[key] != null && (serverItem[key] == null || key.startsWith('_local'))) {
          merged[key] = localItem[key];
        }
      });
      merged.syncStatus = 'synced';
      return merged;
      
    default:
      return serverItem;
  }
};

/**
 * Sync local data with server data
 */
export const syncWithServer = async (storeName, serverData, idKey = 'id') => {
  const localData = await getAllFromStore(storeName);
  const serverMap = new Map(serverData.map(item => [item[idKey], item]));
  const localMap = new Map(localData.map(item => [item[idKey], item]));
  
  const result = {
    added: [],
    updated: [],
    conflicts: [],
  };
  
  // Process server items
  for (const serverItem of serverData) {
    const id = serverItem[idKey];
    const localItem = localMap.get(id);
    
    if (!localItem) {
      // New item from server
      await saveToStore(storeName, { ...serverItem, syncStatus: 'synced' });
      result.added.push(serverItem);
    } else if (localItem.syncStatus === 'pending') {
      // Conflict: local changes not yet synced
      const resolved = resolveConflict(localItem, serverItem);
      await saveToStore(storeName, resolved);
      result.conflicts.push({ local: localItem, server: serverItem, resolved });
    } else {
      // Update with server data
      await saveToStore(storeName, { ...serverItem, syncStatus: 'synced' });
      result.updated.push(serverItem);
    }
  }
  
  // Handle local-only items (keep them)
  for (const [id, localItem] of localMap) {
    if (!serverMap.has(id) && localItem.syncStatus === 'pending') {
      // Keep local-only pending items
      continue;
    }
    if (!serverMap.has(id) && localItem._isLocalOnly) {
      // Keep local-only items
      continue;
    }
  }
  
  return result;
};

/**
 * Check if IndexedDB is available
 */
export const isIndexedDBAvailable = () => {
  return typeof indexedDB !== 'undefined';
};

/**
 * Get storage usage estimate
 */
export const getStorageEstimate = async () => {
  if ('storage' in navigator && 'estimate' in navigator.storage) {
    const estimate = await navigator.storage.estimate();
    return {
      usage: estimate.usage,
      quota: estimate.quota,
      percentUsed: ((estimate.usage / estimate.quota) * 100).toFixed(2),
    };
  }
  return null;
};

export default {
  openDatabase,
  getAllFromStore,
  getFromStore,
  saveToStore,
  deleteFromStore,
  clearStore,
  getPendingSyncItems,
  markAsSynced,
  resolveConflict,
  syncWithServer,
  isIndexedDBAvailable,
  getStorageEstimate,
  STORES,
  ConflictStrategy,
};

// Named exports for direct imports
export { STORES };
